/** @fileoverview Renders standalone inputs and atomically submitted forms. */

import { useEffect, useRef, useState } from 'react';

import { Button } from '../ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '../ui/field';
import { Input } from '../ui/input';
import { Popover, PopoverContent } from '../ui/popover';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { LoaderCircle, Search } from 'lucide-react';

import type {
  FabrikComponent,
  FabrikFormComponent,
  FabrikInputComponent,
  FabrikInputValue,
  InputSnapshot,
} from '../../types/apps';
import ActionConfirmDialog from './ActionConfirmDialog';
import { ChoiceDropdown } from './ChoiceDropdown';
import { useFabrikGlobals, useValueContext } from './appRuntimeContext';
import {
  getActionSendRecordKeyRefs,
  getActionTargetIds,
  useComponentStateStore,
  useIsActionBlocked,
} from './componentStateStore';
import { findStateComponent } from './helpers';
import { appButtonLook } from './appButton';
import DateTimeField from './DateTimeField';
import InlineAlert from './InlineAlert';
import SegmentedControl from './SegmentedControl';
import { gridContainerProps, gridItemProps } from './componentLayout';
import {
  isMissingRequiredSegmented,
  marksOptionalInputs,
  readInvalidText,
  showsChoiceTiles,
  showsSegmented,
} from './inputLayout';
import type { FieldValues, OpenTarget } from './types';
import { useFieldValuesSearch } from './useFieldValuesSearch';
import { readInitialInputValue, useInputOptions } from './useInputOptions';
import { useRunAction } from './useRunAction';

/* Whitespace and zero-width characters, which a label of only these shows as nothing. */
const INVISIBLE_CHARACTERS = /[\s\u200B-\u200D\u2060\uFEFF]/g;
const SUBMIT_JUSTIFY = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  stretch: 'justify-stretch [&>button]:w-full',
} as const;
/* Picked values apply at once; typed ones wait for blur. Date and time are picked from a popup. */
const immediateTypes = new Set([
  'select',
  'radio',
  'checkbox',
  'switch',
  'lookup',
  'date',
  'time',
  'datetime',
]);
/* How long a standalone input waits after the last key before it applies the typed value. */
const TYPING_PAUSE_MS = 500;
const asText = (value: FabrikInputValue | undefined) =>
  value === undefined ? '' : Array.isArray(value) ? value.join(', ') : String(value);

/**
 * Names one option value for a select or radio group. It stays the same when the options load
 * again, unlike the option's position, and keeps `1` and `"1"` apart.
 * @param value the option's value
 */
const optionKey = (value: string | number | boolean) => `${typeof value}:${String(value)}`;

/* White fields with a border that takes the app colour on hover, so they read as ready to fill. */
const CONTROL_CLASS = 'h-app-control bg-card hover:border-ring/60 dark:bg-card';

function InputControl({
  input,
  fields,
  inputs,
  value,
  formId,
  screenId,
  markOptional = false,
  invalidText,
  selectionPath,
  onChange,
}: {
  input: FabrikInputComponent;
  fields: FieldValues;
  inputs: InputSnapshot;
  value: FabrikInputValue | undefined;
  formId?: string;
  screenId: string;
  /** Marks an optional input with "Optional" instead of marking a required one with a star. */
  markOptional?: boolean;
  /** Why the form refused this input's value on submit. */
  invalidText?: string;
  /** JSON path for selecting this field in the live preview. */
  selectionPath?: string;
  onChange: (value: FabrikInputValue | undefined) => void;
}) {
  const {
    options,
    loading,
    error: optionsError,
    kind: optionsKind,
  } = useInputOptions(input, fields, inputs);
  const error = invalidText ?? optionsError;
  const isChoiceInput =
    input.inputType === 'select' || input.inputType === 'lookup' || input.inputType === 'radio';
  /*
   * A large foundation object is searched on the server; one with no options is typed as a code.
   * This holds for every choice input, a radio group included.
   */
  const searchesValues = isChoiceInput && optionsKind === 'search';
  const typesCode = isChoiceInput && optionsKind === 'text';
  const searchedField =
    searchesValues && input.options && 'systemField' in input.options
      ? input.options.systemField
      : undefined;
  const [draft, setDraft] = useState<{ value: FabrikInputValue | undefined } | null>(null);
  const [query, setQuery] = useState('');
  const [filterText, setFilterText] = useState('');
  const [lookupActive, setLookupActive] = useState(false);
  const lookupRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const current = draft === null ? value : draft.value;
  const valueSearch = useFieldValuesSearch(searchedField, current);
  const constraints = input.constraints ?? {};
  const minLength = 'minLength' in constraints ? constraints.minLength : undefined;
  const maxLength = 'maxLength' in constraints ? constraints.maxLength : undefined;
  const pattern = 'pattern' in constraints ? constraints.pattern : undefined;
  const min = 'min' in constraints ? constraints.min : undefined;
  const max = 'max' in constraints ? constraints.max : undefined;
  const step = 'step' in constraints ? constraints.step : undefined;
  const controlId = `${screenId}-${formId ?? 'standalone'}-${input.id}`;
  const descriptionId = `${controlId}-description`;
  const errorId = `${controlId}-error`;
  const codeHintId = `${controlId}-code-hint`;
  const describedBy =
    [input.description && descriptionId, typesCode && codeHintId, error && errorId]
      .filter(Boolean)
      .join(' ') || undefined;
  const hasLabelText = input.label.replace(INVISIBLE_CHARACTERS, '') !== '';
  const label = (
    <>
      {input.label}
      {!markOptional && input.required && (
        <span className="text-destructive" aria-hidden="true">
          {' '}
          *
        </span>
      )}
      {markOptional && !input.required && (
        <span className="type-caption font-normal text-muted-foreground">Optional</span>
      )}
    </>
  );
  const keyedOptions = options.map((option) => ({ ...option, key: optionKey(option.value) }));
  const staticOptions = input.options && 'items' in input.options ? input.options.items : undefined;
  const selectionOptionPath = (optionIndex: number) =>
    selectionPath
      ? staticOptions
        ? `${selectionPath}/options/items/${optionIndex}`
        : `${selectionPath}/runtime/options/${optionIndex}`
      : undefined;
  const selectionOptionDefinitionPath = () =>
    selectionPath && !staticOptions ? `${selectionPath}/options` : undefined;
  const optionsByKey = new Map(keyedOptions.map((option) => [option.key, option]));
  /* A record can hold a legacy picklist's option id; it picks the option with that id. */
  const currentOption =
    current === undefined || Array.isArray(current)
      ? undefined
      : keyedOptions.find(({ value, optionId }) => value === current || optionId === current);
  const currentKey =
    current === undefined || Array.isArray(current)
      ? undefined
      : currentOption?.key ?? optionKey(current);
  const valueForSelect = currentKey && optionsByKey.has(currentKey) ? currentKey : '';
  const isChoice = input.inputType === 'select' || input.inputType === 'radio';
  /* A search page never holds every value, so only a loaded list can tell a value is gone. */
  const isStale =
    isChoice &&
    optionsKind === 'list' &&
    !loading &&
    !optionsError &&
    options.length > 0 &&
    currentKey !== undefined &&
    !optionsByKey.has(currentKey);

  /**
   * Takes a picked key back to its option's value. While options load, an empty or unknown key
   * is ignored, so a reload cannot clear what the viewer picked; otherwise empty clears.
   * @param key the picked option key, or null when the control clears itself
   */
  const pickOption = (key: unknown) => {
    const option = typeof key === 'string' ? optionsByKey.get(key) : undefined;
    if (option) {
      change(option.value);
    } else if (key === null && !loading) {
      change(undefined);
    }
  };

  useEffect(() => setDraft(null), [input.id, value]);
  useEffect(() => {
    if (input.inputType === 'textarea' && pattern) {
      const valid = new RegExp(pattern).test(asText(current));
      textareaRef.current?.setCustomValidity(
        valid || !current ? '' : 'Value does not match the required pattern.'
      );
    }
  }, [pattern, current, input.inputType]);
  useEffect(() => {
    if (input.inputType === 'lookup' && optionsKind === 'list') {
      const selected = options.find((option) => option.value === current);
      setQuery(selected?.label ?? '');
      setLookupActive(false);
      lookupRef.current?.setCustomValidity(
        input.required && current === undefined ? 'Choose an option.' : ''
      );
    }
  }, [input.inputType, input.required, options, current, optionsKind]);

  const change = (next: FabrikInputValue | undefined) => {
    if (formId) {
      onChange(next);
      return;
    }
    if (immediateTypes.has(input.inputType) && !typesCode) {
      setDraft(null);
      onChange(next);
    } else {
      setDraft({ value: next });
    }
  };

  /* A picked value the loaded options no longer hold (a filter changed) is cleared, not kept. */
  useEffect(() => {
    if (isStale) {
      change(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStale]);
  const commitBlur = () => {
    if (!formId && draft !== null) {
      setDraft(null);
      onChange(draft.value);
    } else if (!formId && input.inputType === 'number' && current === undefined) {
      onChange(undefined);
    }
  };
  /* A standalone input (a search box) also applies once typing pauses, not only on blur. */
  useEffect(() => {
    if (formId || draft === null) return undefined;
    const timer = setTimeout(() => {
      setDraft(null);
      onChange(draft.value);
    }, TYPING_PAUSE_MS);
    return () => clearTimeout(timer);
    // onChange is a new function on every render; the draft alone decides when to apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, formId]);

  let control;
  if (input.inputType === 'textarea') {
    control = (
      <Textarea
        id={controlId}
        ref={textareaRef}
        required={input.required}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        placeholder={input.placeholder}
        className={`${CONTROL_CLASS} min-h-28 resize-y`}
        value={asText(current)}
        minLength={minLength}
        maxLength={maxLength}
        onChange={(event) => {
          if (pattern)
            textareaRef.current?.setCustomValidity(
              new RegExp(pattern).test(event.target.value)
                ? ''
                : 'Value does not match the required pattern.'
            );
          change(event.target.value);
        }}
        onBlur={commitBlur}
      />
    );
  } else if (typesCode) {
    control = (
      <Input
        id={controlId}
        type="text"
        required={input.required}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        placeholder={input.placeholder}
        className={CONTROL_CLASS}
        value={asText(current)}
        onChange={(event) => change(event.target.value)}
        onBlur={commitBlur}
      />
    );
  } else if (searchesValues) {
    const {
      options: foundOptions,
      loading: searching,
      error: searchError,
      hasMore,
      searchText,
      optionsMatchSearchText,
      setSearchText,
      setOpen,
      loadMore,
      selectedLabel,
    } = valueSearch;
    const foundByKey = new Map(foundOptions.map((option) => [optionKey(option.value), option]));
    const foundEntries = [...foundByKey];
    const searchedKey =
      current === undefined || current === '' || Array.isArray(current)
        ? undefined
        : optionKey(String(current));
    control = (
      <ChoiceDropdown
        controlId={controlId}
        inputLabel={input.label}
        describedBy={describedBy}
        invalid={Boolean(error)}
        required={input.required}
        placeholder={input.placeholder || 'Choose an option'}
        items={foundEntries.map(([key, { label: optionLabel }]) => ({ key, label: optionLabel }))}
        selectionPaths={foundEntries.map((_, index) => selectionOptionPath(index))}
        selectionDefinitionPaths={foundEntries.map(selectionOptionDefinitionPath)}
        selectedKey={searchedKey}
        selectedLabel={selectedLabel}
        loading={searching}
        error={searchError}
        filterText={searchText}
        itemsMatchFilter={optionsMatchSearchText}
        onFilterTextChange={setSearchText}
        onOpenChange={setOpen}
        hasMore={hasMore}
        onLoadMore={loadMore}
        onPick={(key) => {
          const picked = foundByKey.get(key);
          if (picked) {
            change(picked.value);
          }
        }}
        className={`${CONTROL_CLASS} w-full`}
      />
    );
  } else if (showsSegmented(input)) {
    control = (
      <SegmentedControl
        controlId={controlId}
        label={input.label}
        required={input.required}
        describedBy={describedBy}
        invalid={Boolean(error)}
        options={keyedOptions}
        selectionPaths={keyedOptions.map((_, index) => selectionOptionPath(index))}
        selectionDefinitionPaths={keyedOptions.map(selectionOptionDefinitionPath)}
        selectedKey={valueForSelect || undefined}
        onPick={pickOption}
      />
    );
  } else if (input.inputType === 'select') {
    const filterNeedle = filterText.trim().toLocaleLowerCase();
    const filteredOptions = filterNeedle
      ? keyedOptions.filter(
          ({ label: optionLabel, value: optionValue }) =>
            optionLabel.toLocaleLowerCase().includes(filterNeedle) ||
            String(optionValue).toLocaleLowerCase().includes(filterNeedle)
        )
      : keyedOptions;
    control = (
      <ChoiceDropdown
        controlId={controlId}
        inputLabel={input.label}
        describedBy={describedBy}
        invalid={Boolean(error)}
        required={input.required}
        disabled={loading}
        placeholder={loading ? 'Loading options…' : 'Choose an option'}
        items={filteredOptions.map(({ key, label: optionLabel }) => ({ key, label: optionLabel }))}
        selectionPaths={filteredOptions.map((option) =>
          selectionOptionPath(keyedOptions.findIndex(({ key }) => key === option.key))
        )}
        selectionDefinitionPaths={filteredOptions.map(selectionOptionDefinitionPath)}
        selectedKey={valueForSelect || undefined}
        selectedLabel={valueForSelect ? optionsByKey.get(valueForSelect)?.label : undefined}
        loading={loading}
        filterText={filterText}
        onFilterTextChange={setFilterText}
        onPick={pickOption}
        className={`${CONTROL_CLASS} w-full`}
      />
    );
  } else if (input.inputType === 'radio') {
    control = (
      <RadioGroup
        name={controlId}
        value={valueForSelect}
        required={input.required}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        onValueChange={pickOption}
        className={showsChoiceTiles(keyedOptions) ? 'grid-cols-2 gap-2 @md:grid-cols-3' : 'gap-2'}
      >
        {keyedOptions.map((option, index) => {
          const optionId = `${controlId}-${index}`;
          return (
            <FieldLabel
              key={option.key}
              htmlFor={optionId}
              data-fabrik-selection-path={selectionOptionPath(index)}
              data-fabrik-selection-definition-path={selectionOptionDefinitionPath()}
              className="min-h-app-control w-full cursor-pointer items-center rounded-lg border bg-card px-3 py-2 type-body font-normal transition-colors hover:border-ring/60 has-data-checked:border-primary/40 has-data-checked:bg-primary/5"
            >
              <RadioGroupItem id={optionId} value={option.key} />
              <span>{option.label}</span>
            </FieldLabel>
          );
        })}
      </RadioGroup>
    );
  } else if (input.inputType === 'lookup') {
    const needle = query.toLocaleLowerCase();
    const filtered = options.filter(
      ({ label: optionLabel, value: optionValue }) =>
        optionLabel.toLocaleLowerCase().includes(needle) ||
        String(optionValue).toLocaleLowerCase().includes(needle)
    );
    control = (
      <Popover open={lookupActive && query.trim().length > 0} onOpenChange={setLookupActive}>
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id={controlId}
            ref={lookupRef}
            type="search"
            required={input.required}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            aria-autocomplete="list"
            aria-expanded={lookupActive && query.trim().length > 0}
            aria-controls={`${controlId}-options`}
            aria-haspopup="listbox"
            placeholder={input.placeholder}
            className={`${CONTROL_CLASS} pl-9 ${loading ? 'pr-9' : ''}`}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setLookupActive(true);
              lookupRef.current?.setCustomValidity(input.required ? 'Choose an option.' : '');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setLookupActive(false);
              if (event.key === 'ArrowDown' && lookupActive) {
                event.preventDefault();
                document
                  .getElementById(`${controlId}-options`)
                  ?.querySelector<HTMLButtonElement>('[role="option"]')
                  ?.focus();
              }
            }}
          />
          {loading && (
            <LoaderCircle
              className="absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
              aria-hidden="true"
            />
          )}
        </div>
        <PopoverContent
          anchor={lookupRef.current}
          id={`${controlId}-options`}
          className="max-h-60 w-(--anchor-width) overflow-auto p-1"
          role="listbox"
          aria-label={`${input.label} options`}
        >
          {filtered.map((option) => {
            const optionIndex = keyedOptions.findIndex(
              ({ value: optionValue }) => optionValue === option.value
            );
            return (
              <Button
                key={`${optionIndex}-${String(option.value)}`}
                data-fabrik-selection-path={selectionOptionPath(optionIndex)}
                data-fabrik-selection-definition-path={selectionOptionDefinitionPath()}
                type="button"
                variant="ghost"
                role="option"
                aria-selected={option.value === current}
                className="h-auto w-full justify-start px-3 py-2 text-left font-normal"
                onClick={() => {
                  change(option.value);
                  setQuery(option.label);
                  setLookupActive(false);
                  lookupRef.current?.setCustomValidity('');
                }}
              >
                {option.label}
              </Button>
            );
          })}
          {loading && filtered.length === 0 && (
            <p className="px-3 py-2 type-body text-muted-foreground" role="status">
              Loading options…
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="px-3 py-2 type-body text-muted-foreground" role="status">
              No matching options.
            </p>
          )}
        </PopoverContent>
      </Popover>
    );
  } else if (input.inputType === 'checkbox' || input.inputType === 'switch') {
    const Control = input.inputType === 'switch' ? Switch : Checkbox;
    /* A settings row: the control sits left of its label, with no box around it. */
    control = (
      <Field
        data-fabrik-selection-path={formId ? selectionPath : undefined}
        data-invalid={error ? true : undefined}
        orientation="horizontal"
        className="min-h-app-control items-start gap-3 py-1.5"
      >
        <Control
          id={controlId}
          name={controlId}
          required={input.required}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          checked={current === true}
          onCheckedChange={(checked) => change(checked === true)}
          /* An empty checkbox is white, so it does not look switched off. */
          className={input.inputType === 'checkbox' ? 'mt-0.5 bg-card dark:bg-card' : 'mt-0.5'}
        />
        <FieldContent>
          <FieldLabel
            htmlFor={controlId}
            data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}
            className="font-medium"
          >
            {label}
          </FieldLabel>
          {input.description && (
            <FieldDescription
              id={descriptionId}
              data-fabrik-selection-path={
                selectionPath ? `${selectionPath}/description` : undefined
              }
            >
              {input.description}
            </FieldDescription>
          )}
          {error && <FieldError id={errorId}>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  } else if (
    input.inputType === 'date' ||
    input.inputType === 'time' ||
    input.inputType === 'datetime'
  ) {
    control = (
      <DateTimeField
        controlId={controlId}
        inputType={input.inputType}
        value={asText(current)}
        onChange={change}
        onCommit={commitBlur}
        required={input.required}
        describedBy={describedBy}
        invalid={Boolean(error)}
        placeholder={input.placeholder}
        min={typeof min === 'string' ? min : undefined}
        max={typeof max === 'string' ? max : undefined}
        step={step}
      />
    );
  } else {
    const valueText =
      input.inputType === 'number' && typeof current === 'number'
        ? String(current)
        : asText(current);
    control = (
      <Input
        id={controlId}
        type={input.inputType}
        required={input.required}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        placeholder={input.placeholder}
        className={CONTROL_CLASS}
        value={valueText}
        min={min}
        max={max}
        step={step}
        minLength={minLength}
        maxLength={maxLength}
        pattern={pattern}
        onChange={(event) =>
          change(
            input.inputType === 'number'
              ? event.target.value === ''
                ? undefined
                : Number.isFinite(event.target.valueAsNumber)
                ? event.target.valueAsNumber
                : undefined
              : event.target.value
          )
        }
        onBlur={commitBlur}
      />
    );
  }

  if (input.inputType === 'checkbox' || input.inputType === 'switch') return control;
  if (input.inputType === 'radio' && !searchesValues && !typesCode) {
    return (
      <FieldSet
        data-fabrik-selection-path={formId ? selectionPath : undefined}
        data-invalid={error ? true : undefined}
      >
        <FieldLegend
          variant="label"
          data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}
        >
          {label}
        </FieldLegend>
        {control}
        {input.description && (
          <FieldDescription
            id={descriptionId}
            data-fabrik-selection-path={selectionPath ? `${selectionPath}/description` : undefined}
          >
            {input.description}
          </FieldDescription>
        )}
        {loading && <FieldDescription role="status">Loading options…</FieldDescription>}
        {error && <FieldError id={errorId}>{error}</FieldError>}
      </FieldSet>
    );
  }

  return (
    <Field
      data-fabrik-selection-path={formId ? selectionPath : undefined}
      data-invalid={error ? true : undefined}
    >
      {/* A label of only spaces or zero-width characters shows nothing, so it takes no row either. */}
      {hasLabelText && (
        <FieldLabel
          htmlFor={controlId}
          data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}
        >
          {label}
        </FieldLabel>
      )}
      {control}
      {input.description && (
        <FieldDescription
          id={descriptionId}
          data-fabrik-selection-path={selectionPath ? `${selectionPath}/description` : undefined}
        >
          {input.description}
        </FieldDescription>
      )}
      {typesCode && <FieldDescription id={codeHintId}>Type the code.</FieldDescription>}
      {loading && input.inputType !== 'select' && input.inputType !== 'lookup' && (
        <FieldDescription role="status">Loading options…</FieldDescription>
      )}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </Field>
  );
}

export function StandaloneInput({
  input,
  screenId,
  fields,
  inputs,
  commitStandaloneInput,
  selectionPath,
}: {
  input: FabrikInputComponent;
  screenId: string;
  fields: FieldValues;
  inputs: InputSnapshot;
  commitStandaloneInput: (inputId: string, value: FabrikInputValue | undefined) => void;
  selectionPath?: string;
}) {
  const { variables } = useValueContext();
  const globals = useFabrikGlobals();
  const value =
    inputs.standalone?.[input.id] ??
    readInitialInputValue(input, fields, inputs, globals, variables);
  return (
    <FieldGroup>
      <InputControl
        input={input}
        screenId={screenId}
        fields={fields}
        inputs={inputs}
        value={value}
        selectionPath={selectionPath}
        onChange={(next) => commitStandaloneInput(input.id, next)}
      />
    </FieldGroup>
  );
}

/* Component kinds that sit beside a form without making the screen wide. */
const SMALL_KINDS = new Set<FabrikComponent['kind']>(['text', 'icon', 'button', 'input']);

/** True when the form sits directly on the screen and every other block there is small. */
const isAloneOnScreen = (formId: string, screenComponents: FabrikComponent[]) =>
  screenComponents.some(({ id }) => id === formId) &&
  screenComponents.every(({ id, kind }) => id === formId || SMALL_KINDS.has(kind));

export function FormRenderer({
  component,
  screenId,
  screenComponents,
  selectionPath,
  fields,
  inputs,
  setInputs,
  onOpenTarget,
}: {
  component: FabrikFormComponent;
  screenId: string;
  screenComponents: FabrikComponent[];
  fields: FieldValues;
  inputs: InputSnapshot;
  setInputs: (update: (current: InputSnapshot) => InputSnapshot) => void;
  onOpenTarget: OpenTarget;
  selectionPath?: string;
}) {
  const { variables } = useValueContext();
  const globals = useFabrikGlobals();
  const { actions } = useComponentStateStore();
  /* A form whose action the viewer may not run is not shown at all. */
  const submitTarget = component.submit.target;
  const isSubmitBlocked = useIsActionBlocked(
    submitTarget?.type === 'action' ? submitTarget.actionId : ''
  );
  const { isBusy, error, runAction, confirmation } = useRunAction();
  const [draft, setDraft] = useState<Record<string, FabrikInputValue | undefined>>({});
  const valueOfInput = (input: FabrikInputComponent) =>
    Object.hasOwn(draft, input.id)
      ? draft[input.id]
      : inputs.forms?.[component.id]?.[input.id] ??
        readInitialInputValue(input, fields, inputs, globals, variables);
  const workingFormValues: Record<string, FabrikInputValue> = {};
  component.inputs.forEach((input) => {
    const value = valueOfInput(input);
    if (value !== undefined) workingFormValues[input.id] = value;
  });
  /* Inputs in no section come first, then each section with its inputs in the order it names them. */
  const inputsById = new Map(component.inputs.map((input) => [input.id, input]));
  const sectioned = new Set((component.sections ?? []).flatMap(({ inputIds }) => inputIds));
  const loose = component.inputs.filter(({ id }) => !sectioned.has(id));
  const inputGroups = [
    ...(loose.length ? [{ key: 'inputs', inputs: loose }] : []),
    ...(component.sections ?? []).map(({ title, note, inputIds }, sectionIndex) => ({
      key: `section-${sectionIndex}`,
      title,
      note,
      inputs: inputIds.flatMap((id) => inputsById.get(id) ?? []),
    })),
  ] as { key: string; title?: string; note?: string; inputs: FabrikInputComponent[] }[];
  const workingInputs = {
    ...inputs,
    forms: { ...inputs.forms, [component.id]: workingFormValues },
  };
  const markOptional = marksOptionalInputs(component.inputs);
  /* Sections get numbers when there are two or more, so the order reads as steps. */
  const isNumbered = (component.sections?.length ?? 0) > 1;
  const layout = component.layout;
  const sectionByInputId = new Map(
    (component.sections ?? []).flatMap(({ inputIds }, sectionIndex) =>
      inputIds.map((inputId) => [inputId, sectionIndex] as const)
    )
  );
  const renderedSections = new Set<number>();
  const controlIdPrefix = `${screenId}-${component.id}-`;
  /**
   * Finds the input a control belongs to from its id or name. A radio item's id ends in its
   * position, so a trailing `-<n>` is tried as well.
   * @param element the control the browser found not valid
   */
  const findInputOfControl = (element: Element) => {
    const keys = [element.id, element.getAttribute('name') ?? '']
      .filter((key) => key.startsWith(controlIdPrefix))
      .map((key) => key.slice(controlIdPrefix.length));
    const withoutPosition = keys.map((key) => key.replace(/-\d+$/, ''));
    return [...keys, ...withoutPosition].flatMap((key) => inputsById.get(key) ?? [])[0];
  };
  /* The browser's own bubbles are off; each refused value is worded under its field instead. */
  const invalidFound = useRef<Record<string, string> | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const noteInvalid = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const element = event.target as HTMLInputElement;
    const input = findInputOfControl(element);
    if (input && invalidFound.current && !invalidFound.current[input.id]) {
      invalidFound.current[input.id] = readInvalidText(input, element);
    }
  };
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    invalidFound.current = {};
    event.currentTarget.checkValidity();
    const found = invalidFound.current;
    invalidFound.current = null;
    /* A segmented control has no native input, so the browser check cannot see it is empty. */
    component.inputs.forEach((input) => {
      if (isMissingRequiredSegmented(input, valueOfInput(input))) {
        found[input.id] = `Choose a value for ${input.label}.`;
      }
    });
    setInvalid(found);
    const first = component.inputs.find(({ id }) => found[id]);
    if (first) {
      const firstId = `${controlIdPrefix}${first.id}`;
      const control = showsSegmented(first)
        ? document.getElementById(firstId)?.querySelector<HTMLButtonElement>('button')
        : document.getElementById(firstId) ?? document.getElementById(`${firstId}-0`);
      control?.focus();
      control?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    const captured: Record<string, FabrikInputValue> = {};
    component.inputs.forEach((input) => {
      const value = valueOfInput(input);
      if (value !== undefined) captured[input.id] = value;
    });
    setInputs((current) => ({ ...current, forms: { ...current.forms, [component.id]: captured } }));
    const { target } = component.submit;
    if (target?.type === 'action') {
      const { actionId } = target;
      runAction({
        actionId,
        screenId,
        fields,
        inputs: { ...inputs, forms: { ...inputs.forms, [component.id]: captured } },
        targets: getActionTargetIds(actions, actionId).flatMap(
          (componentId) => findStateComponent(screenComponents, componentId) ?? []
        ),
        recordKeyRefs: getActionSendRecordKeyRefs(actions, actionId, screenComponents),
      });
      return;
    }
    if (target) onOpenTarget(target, fields);
  };
  const submitButton = (
    <Button
      type="submit"
      data-fabrik-selection-path={selectionPath ? `${selectionPath}/submit` : undefined}
      {...appButtonLook({ variant: component.submit.style.variant })}
      disabled={isBusy}
      aria-busy={isBusy || undefined}
    >
      {isBusy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
      <span
        data-fabrik-selection-path={selectionPath ? `${selectionPath}/submit/label` : undefined}
      >
        {component.submit.label}
      </span>
    </Button>
  );
  /**
   * Keeps a changed value and clears the error shown for it.
   * @param inputId the input that changed
   * @param value its new value
   */
  const changeInput = (inputId: string, value: FabrikInputValue | undefined) => {
    setDraft((current) => ({ ...current, [inputId]: value }));
    setInvalid(({ [inputId]: _cleared, ...rest }) => rest);
  };
  const renderInput = (input: FabrikInputComponent) => (
    <InputControl
      key={input.id}
      input={input}
      screenId={screenId}
      formId={component.id}
      fields={fields}
      inputs={workingInputs}
      value={valueOfInput(input)}
      markOptional={markOptional}
      invalidText={invalid[input.id]}
      selectionPath={
        selectionPath ? `${selectionPath}/inputs/${component.inputs.indexOf(input)}` : undefined
      }
      onChange={(value) => changeInput(input.id, value)}
    />
  );
  const isNarrowForm =
    !(layout && (layout.mode === 'flex' || (layout.columns ?? 0) > 2)) &&
    isAloneOnScreen(component.id, screenComponents);
  let sectionNumber = 0;
  if (isSubmitBlocked) {
    return null;
  }
  return (
    <form
      onSubmit={submit}
      onInvalidCapture={noteInvalid}
      noValidate
      /* A form alone on its screen stays at a readable width, centered; next to wide blocks it fills the row. */
      className={isNarrowForm ? 'mx-auto w-full max-w-3xl' : undefined}
    >
      <Card className="gap-0 py-0 shadow-sm">
        {component.title && (
          <CardHeader className="px-6 pt-5 pb-0">
            <CardTitle
              data-fabrik-selection-path={selectionPath ? `${selectionPath}/title` : undefined}
              role="heading"
              aria-level={3}
              className="type-heading font-semibold tracking-tight"
            >
              {component.title}
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className="@container flex flex-col px-6 pt-4 pb-5">
          {layout ? (
            <div {...gridContainerProps(layout)}>
              {layout.items.flatMap((item, layoutIndex) => {
                const input = item.type === 'input' ? inputsById.get(item.inputId) : undefined;
                const content =
                  item.type === 'submit' ? (
                    /* The cell aligns up and down; the button also follows the align left to right. */
                    <div className={`flex ${SUBMIT_JUSTIFY[item.align ?? 'start']}`}>
                      {submitButton}
                    </div>
                  ) : (
                    input && renderInput(input)
                  );
                const sectionIndex =
                  item.type === 'input' ? sectionByInputId.get(item.inputId) : undefined;
                const section =
                  sectionIndex === undefined ? undefined : component.sections?.[sectionIndex];
                let sectionHeading = null;
                const sectionCell = gridItemProps(layout.mode, {
                  span: layout.columns,
                  fullRow: true,
                });
                if (section && sectionIndex !== undefined && !renderedSections.has(sectionIndex)) {
                  /* Every section after the first gets a line and extra room, so sections do not run together. */
                  const isLaterSection = renderedSections.size > 0;
                  renderedSections.add(sectionIndex);
                  sectionHeading = (
                    <div
                      key={`section-${sectionIndex}`}
                      data-fabrik-selection-path={
                        selectionPath ? `${selectionPath}/sections/${sectionIndex}` : undefined
                      }
                      {...sectionCell}
                      className={`${sectionCell.className} ${
                        isLaterSection ? 'mt-4 border-t border-border pt-6' : ''
                      }`}
                    >
                      <h4
                        data-fabrik-selection-path={
                          selectionPath
                            ? `${selectionPath}/sections/${sectionIndex}/title`
                            : undefined
                        }
                        className="flex items-center gap-3 type-body font-semibold"
                      >
                        {isNumbered && (
                          <span
                            className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 type-caption font-semibold text-primary tabular-nums"
                            aria-hidden="true"
                          >
                            {sectionIndex + 1}
                          </span>
                        )}
                        {section.title}
                      </h4>
                      {section.note && (
                        <FieldDescription
                          data-fabrik-selection-path={
                            selectionPath
                              ? `${selectionPath}/sections/${sectionIndex}/note`
                              : undefined
                          }
                          className="mt-0.5"
                        >
                          {section.note}
                        </FieldDescription>
                      )}
                    </div>
                  );
                }
                const cell = (
                  <div
                    key={item.type === 'submit' ? 'submit' : item.inputId}
                    data-fabrik-selection-path={
                      selectionPath ? `${selectionPath}/layout/items/${layoutIndex}` : undefined
                    }
                    {...gridItemProps(layout.mode, item)}
                  >
                    {content}
                  </div>
                );
                return sectionHeading ? [sectionHeading, cell] : [cell];
              })}
            </div>
          ) : (
            inputGroups.map(({ key, title, note, inputs: groupInputs }) => {
              const controls = (
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 @lg:grid-cols-2">
                  {groupInputs.map((input) => (
                    <div
                      key={input.id}
                      /* A wide form gives each input half a row unless it is set to take a full row. */
                      className={input.fullRow ? '@lg:col-span-2' : undefined}
                    >
                      {renderInput(input)}
                    </div>
                  ))}
                </div>
              );
              if (!title) {
                return (
                  <div key={key} className="pb-4">
                    {controls}
                  </div>
                );
              }
              sectionNumber += 1;
              const indent = isNumbered ? '@lg:pl-10' : '';
              const headingId = `${controlIdPrefix}${key}-title`;
              /* A plain group, not a fieldset: a fieldset draws its top border through the legend. */
              return (
                <div
                  key={key}
                  role="group"
                  aria-labelledby={headingId}
                  data-fabrik-selection-path={
                    selectionPath
                      ? `${selectionPath}/sections/${Number(key.slice('section-'.length))}`
                      : undefined
                  }
                  className="border-t py-4 first:border-t-0 first:pt-0 last:pb-0"
                >
                  <h4
                    id={headingId}
                    data-fabrik-selection-path={
                      selectionPath
                        ? `${selectionPath}/sections/${Number(key.slice('section-'.length))}/title`
                        : undefined
                    }
                    className="flex items-center gap-3 type-body font-semibold"
                  >
                    {isNumbered && (
                      <span
                        className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 type-caption font-semibold text-primary tabular-nums"
                        aria-hidden="true"
                      >
                        {sectionNumber}
                      </span>
                    )}
                    {title}
                  </h4>
                  {note && (
                    <FieldDescription
                      data-fabrik-selection-path={
                        selectionPath
                          ? `${selectionPath}/sections/${Number(key.slice('section-'.length))}/note`
                          : undefined
                      }
                      className={`mt-0.5 ${indent}`}
                    >
                      {note}
                    </FieldDescription>
                  )}
                  <div className={`mt-3 ${indent}`}>{controls}</div>
                </div>
              );
            })
          )}
        </CardContent>
        {error && (
          <InlineAlert variant="box" className="mx-6 mb-4">
            {error}
          </InlineAlert>
        )}
        {/* With a layout the submit button sits in the grid, so the form needs no footer. */}
        {!layout && (
          <CardFooter className="flex-wrap justify-end gap-3 bg-muted/30 px-6 py-4">
            {submitButton}
          </CardFooter>
        )}
        <ActionConfirmDialog {...confirmation} />
      </Card>
    </form>
  );
}
